import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { client } from "@/lib/api";
import { X, Wind, Check, ArrowRight, RotateCcw, MessageCircleHeart, Wrench, Sparkles, HandHeart } from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.28, ease: "easeOut" },
};

// Static fallbacks so the flow works instantly even before the session call resolves.
const TRIGGERS_FALLBACK = [
  { id: "craving", label: "Strong physical craving" },
  { id: "stress", label: "Stress" },
  { id: "habit", label: "Habit / automatic" },
  { id: "after_eating", label: "After eating" },
  { id: "driving", label: "Driving" },
  { id: "social", label: "Social situation" },
  { id: "boredom", label: "Boredom" },
  { id: "emotional", label: "Emotional moment" },
  { id: "alcohol_or_other", label: "Alcohol / another trigger" },
  { id: "something_else", label: "Something else" },
];
const RENEW_FALLBACK = [
  { line: "You don't have to win tomorrow right now. Just this moment.", verse: "I can do all things through Christ who strengthens me.", ref: "Philippians 4:13" },
  { line: "This craving is loud, but it is not the boss of you.", verse: "The Lord is my strength and my shield; my heart trusts in him.", ref: "Psalm 28:7" },
  { line: "One moment at a time is enough. That's all that's being asked of you.", verse: "His mercies are new every morning.", ref: "Lamentations 3:23" },
];
const PRAYERS_FALLBACK = [
  "Lord, I can't carry this craving alone — would you carry it with me? Just for this moment, be my strength. Amen.",
  "Father, quiet my body and my mind. Help me wait out this wave and trust that you are near. Amen.",
];

export default function BreakChainFlow({ open, planId, substance = "cigarettes", onResisted, onLogAnyway, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("pause");
  const [sessionId, setSessionId] = useState(null);
  const [triggers, setTriggers] = useState(TRIGGERS_FALLBACK);
  const [usual, setUsual] = useState([]);
  const [renewPool, setRenewPool] = useState(RENEW_FALLBACK);
  const [prayers, setPrayers] = useState(PRAYERS_FALLBACK);
  const [trigger, setTrigger] = useState(null);
  const [shown, setShown] = useState([]);
  const [current, setCurrent] = useState(null);
  const [prevWorked, setPrevWorked] = useState(false);
  const [workedCount, setWorkedCount] = useState(0);
  const [loadingIx, setLoadingIx] = useState(false);
  const [renewPick, setRenewPick] = useState(null);
  const [prayer, setPrayer] = useState(null);
  const [breathing, setBreathing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [shareName, setShareName] = useState(true);
  const [prayerSent, setPrayerSent] = useState(false);
  const [sendingPrayer, setSendingPrayer] = useState(false);
  const initRef = useRef(false);

  const reset = useCallback(() => {
    setStep("pause"); setSessionId(null); setTriggers(TRIGGERS_FALLBACK); setUsual([]); setRenewPool(RENEW_FALLBACK);
    setPrayers(PRAYERS_FALLBACK); setTrigger(null); setShown([]); setCurrent(null); setPrevWorked(false);
    setWorkedCount(0); setLoadingIx(false); setRenewPick(null); setPrayer(null);
    setBreathing(false); setEnding(false);
    setShareName(true); setPrayerSent(false); setSendingPrayer(false);
  }, []);

  useEffect(() => {
    if (!open) { initRef.current = false; reset(); return; }
    if (initRef.current) return;   // one session POST per open (guards StrictMode double-effect)
    initRef.current = true;
    let active = true;
    (async () => {
      try {
        const { data } = await client.post("/break-chain/session", { plan_id: planId });
        if (!active) return;
        setSessionId(data.session_id);
        setTriggers(data.triggers || []);
        setUsual(data.usual_triggers || []);
        setRenewPool(data.renew || []);
        setPrayers(data.prayers || []);
      } catch (e) { /* PAUSE still works without session; events just won't record */ }
    })();
    return () => { active = false; };
  }, [open, planId, reset]);

  const ev = useCallback((event, extra = {}) => {
    if (!sessionId) return;
    client.post("/break-chain/event", { session_id: sessionId, event, ...extra }).catch(() => {});
  }, [sessionId]);

  if (!open) return null;

  const noun = { cigarettes: "cigarette", vaping: "vape", chew: "dip" }[substance] || "nicotine";

  const fetchIntervention = async (trig, exclude) => {
    setLoadingIx(true);
    try {
      const { data } = await client.post("/break-chain/intervention", { session_id: sessionId, trigger: trig, exclude });
      setCurrent(data.intervention);
      setPrevWorked(!!data.previously_worked);
      setWorkedCount(data.worked_count || 0);
      setShown((s) => Array.from(new Set([...s, data.intervention.title])));
    } catch (e) { /* keep prior */ }
    setLoadingIx(false);
  };

  const startBreathing = () => setBreathing(true);

  const pickTrigger = async (tid) => {
    setTrigger(tid);
    ev("trigger_selected", { trigger: tid });
    setStep("interrupt");
    await fetchIntervention(tid, []);
  };

  const goRenew = () => {
    ev("intervention_accepted", { trigger, intervention: current?.title });
    if (!renewPick && renewPool.length) setRenewPick(renewPool[Math.floor(Math.random() * renewPool.length)]);
    ev("renew_reached");
    setStep("renew");
  };

  const openPrayer = () => {
    if (prayers.length) setPrayer(prayers[Math.floor(Math.random() * prayers.length)]);
    ev("prayer_opened");
  };

  const sendPrayer = async () => {
    if (sendingPrayer) return;
    setSendingPrayer(true);
    try {
      await client.post("/prayer/request", { trigger, substance, anonymous: !shareName, session_id: sessionId });
      setPrayerSent(true);
    } catch (e) { /* ignore */ }
    setSendingPrayer(false);
  };

  const madeIt = async () => {
    if (ending) return; setEnding(true);
    ev("craving_overcome", { outcome: "made_it", trigger, intervention: current?.title });
    try { await onResisted?.(); } finally { onClose?.(); }
  };

  const logIt = async () => {
    if (ending) return; setEnding(true);
    ev("nicotine_logged_after_intervention", { outcome: "logged", trigger });
    try { await onLogAnyway?.(); } finally { onClose?.(); }
  };

  const toCompanion = () => { ev("companion_escalation"); onClose?.(); navigate("/app/coach"); };
  const toToolkit = () => { onClose?.(); navigate("/app/journey"); };

  const QuietLog = ({ id }) => (
    <button data-testid={id} onClick={logIt} disabled={ending}
      className="mt-6 w-full text-center text-sm text-stone hover:text-olive transition-colors py-2">
      I already had one — log it, no shame
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-[#FBF8F2] overflow-y-auto" data-testid="break-chain-flow">
      <button data-testid="bc-close" onClick={onClose} aria-label="Close"
        className="absolute top-4 right-4 rounded-full p-2 text-stone hover:bg-black/5 transition-colors z-10">
        <X className="w-6 h-6" />
      </button>

      <div className="min-h-full flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">

            {/* PAUSE */}
            {step === "pause" && (
              <motion.div key="pause" {...fade} className="text-center" data-testid="bc-pause">
                <div className="flex justify-center mb-8">
                  <motion.div
                    animate={breathing ? { scale: [1, 1.6, 1.6, 1] } : { scale: [1, 1.08, 1] }}
                    transition={breathing ? { duration: 8, repeat: Infinity, ease: "easeInOut", times: [0, 0.4, 0.6, 1] } : { duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="rounded-full bg-brand-teal/15 flex items-center justify-center"
                    style={{ width: 140, height: 140 }}>
                    <div className="rounded-full bg-brand-teal/25 flex items-center justify-center" style={{ width: 88, height: 88 }}>
                      <Wind className="w-8 h-8 text-brand-teal-dark" />
                    </div>
                  </motion.div>
                </div>
                <p className="uppercase tracking-[0.28em] text-xs text-terracotta mb-3">Pause</p>
                {!breathing ? (
                  <>
                    <h1 className="font-serif text-4xl text-olive leading-tight mb-3">This urge is a wave</h1>
                    <p className="text-olive/70 mb-2">It will rise, crest, and pass — usually within a few minutes — whether or not you {noun}.</p>
                    <p className="text-olive/70 mb-8">You're not deciding about forever. Only the next few minutes.</p>
                    <button data-testid="bc-pause-start" onClick={startBreathing}
                      className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-white py-4 text-base font-medium transition-colors">
                      Breathe through it with me
                    </button>
                    <button data-testid="bc-pause-skip" onClick={() => { ev("pause_completed"); setStep("understand"); }}
                      className="mt-3 w-full text-center text-sm text-stone hover:text-olive hover:underline py-2">
                      Skip the breath
                    </button>
                  </>
                ) : (
                  <>
                    <h1 className="font-serif text-4xl text-olive leading-tight mb-3">Breathe slowly</h1>
                    <p className="text-olive/70 mb-8">In through your nose… and slowly out through your mouth. Let the wave rise and fall — you're still in charge.</p>
                    <button data-testid="bc-pause-continue" onClick={() => { ev("pause_completed"); setStep("understand"); }}
                      className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-white py-4 text-base font-medium transition-colors">
                      I'm steadier — continue
                    </button>
                  </>
                )}
                <QuietLog id="bc-log-pause" />
              </motion.div>
            )}

            {/* UNDERSTAND */}
            {step === "understand" && (
              <motion.div key="understand" {...fade} data-testid="bc-understand">
                <p className="uppercase tracking-[0.28em] text-xs text-terracotta mb-3 text-center">Understand</p>
                <h1 className="font-serif text-3xl text-olive leading-tight mb-2 text-center">
                  Name what's driving it
                </h1>
                <p className="text-olive/70 text-sm text-center mb-6 max-w-sm mx-auto">
                  Naming a craving loosens its grip. This is a trigger — not a command you have to obey.
                </p>
                {usual.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs text-stone mb-2 text-center">Is it one of your usual triggers?</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {usual.map((u, i) => (
                        <button key={i} data-testid={`bc-usual-${i}`} onClick={() => pickTrigger(u.trigger_id)}
                          className="rounded-full bg-sage border border-brand-teal/30 text-brand-teal-dark px-4 py-2.5 text-sm font-medium hover:bg-brand-teal/10 transition-colors">
                          {u.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-stone mt-4 mb-1 text-center">or</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2.5">
                  {triggers.map((t) => (
                    <button key={t.id} data-testid={`bc-trigger-${t.id}`} onClick={() => pickTrigger(t.id)}
                      className="rounded-2xl bg-card border border-border px-3 py-4 text-sm text-olive text-center hover:border-brand-teal/50 hover:bg-sage transition-colors min-h-[60px] flex items-center justify-center">
                      {t.label}
                    </button>
                  ))}
                </div>
                <QuietLog id="bc-log-understand" />
              </motion.div>
            )}

            {/* INTERRUPT */}
            {step === "interrupt" && (
              <motion.div key="interrupt" {...fade} data-testid="bc-interrupt">
                <p className="uppercase tracking-[0.28em] text-xs text-terracotta mb-3 text-center">Interrupt</p>
                {prevWorked && (
                  <div className="rounded-2xl bg-brand-teal/10 border border-brand-teal/30 p-3 mb-4 text-center" data-testid="bc-previously-worked">
                    <p className="text-sm text-brand-teal-dark">
                      This has helped you before — you've made it through {workedCount} {workedCount === 1 ? "time" : "times"} this way.
                    </p>
                  </div>
                )}
                <div className="rounded-3xl bg-card border border-border p-7 shadow-soft mb-6 min-h-[140px] flex flex-col justify-center">
                  {loadingIx ? (
                    <p className="text-center text-stone">Finding something that fits…</p>
                  ) : (
                    <>
                      <p className="text-xs text-stone mb-2">Your way out of this one</p>
                      <h1 className="font-serif text-2xl text-olive mb-2">{current?.title || "Change the pattern"}</h1>
                      <p className="text-olive/75 leading-relaxed">{current?.detail}</p>
                    </>
                  )}
                </div>
                <button data-testid="bc-try" onClick={goRenew} disabled={loadingIx}
                  className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-white py-4 text-base font-medium transition-colors disabled:opacity-50">
                  I'll do this now
                </button>
                <button data-testid="bc-another" onClick={() => { ev("another_intervention_requested", { trigger }); fetchIntervention(trigger, shown); }} disabled={loadingIx}
                  className="mt-3 w-full rounded-full border border-border text-olive py-3.5 text-sm hover:bg-sage transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  <RotateCcw className="w-4 h-4" /> Give me another option
                </button>
                <button data-testid="bc-companion-link" onClick={toCompanion}
                  className="mt-4 w-full text-center text-sm text-brand-teal-dark hover:underline py-1">
                  None of these fit? Talk it through with Companion
                </button>
                <QuietLog id="bc-log-interrupt" />
              </motion.div>
            )}

            {/* RENEW */}
            {step === "renew" && (
              <motion.div key="renew" {...fade} data-testid="bc-renew">
                <div className="rounded-3xl bg-brand-navy text-brand-ivory p-8 shadow-soft border border-brand-gold/20 text-center">
                  <p className="uppercase tracking-[0.28em] text-xs text-gold mb-4">Renew</p>
                  <p className="font-serif text-2xl leading-snug mb-6">{renewPick?.line || "You don't have to win tomorrow right now. Just this moment."}</p>
                  <p className="font-scripture italic text-scripture-lg text-brand-ivory-muted mb-1">"{renewPick?.verse || "I can do all things through Christ who strengthens me."}"</p>
                  <p className="text-xs uppercase tracking-[0.18em] text-gold/80">{renewPick?.ref || "Philippians 4:13"}</p>

                  {prayer ? (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="bc-prayer-text"
                      className="mt-7 pt-6 border-t border-white/10 text-brand-ivory/90 leading-relaxed">
                      {prayer}
                    </motion.p>
                  ) : (
                    <button data-testid="bc-pray" onClick={openPrayer}
                      className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-brand-ivory px-5 py-2.5 text-sm transition-colors">
                      <HandHeart className="w-4 h-4 text-gold" /> Pray through this moment
                    </button>
                  )}
                </div>
                <button data-testid="bc-renew-continue" onClick={() => setStep("outcome")}
                  className="mt-6 w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-white py-4 text-base font-medium transition-colors flex items-center justify-center gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
                <QuietLog id="bc-log-renew" />
              </motion.div>
            )}

            {/* OUTCOME */}
            {step === "outcome" && (
              <motion.div key="outcome" {...fade} className="text-center" data-testid="bc-outcome">
                <div className="flex justify-center mb-6">
                  <div className="rounded-full bg-sage p-4"><Sparkles className="w-7 h-7 text-brand-teal-dark" /></div>
                </div>
                <h1 className="font-serif text-3xl text-olive mb-8">How are you doing now?</h1>
                <button data-testid="bc-made-it" onClick={madeIt} disabled={ending}
                  className="w-full rounded-full bg-brand-teal hover:bg-brand-teal/90 text-brand-navy py-4 text-base font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  <Check className="w-5 h-5" /> I made it through
                </button>
                <button data-testid="bc-still-strong" onClick={() => { ev("craving_still_strong"); setStep("struggle"); }}
                  className="mt-3 w-full rounded-full border border-border text-olive py-3.5 text-sm hover:bg-sage transition-colors">
                  The urge is still strong
                </button>
                <QuietLog id="bc-log-outcome" />
              </motion.div>
            )}

            {/* STILL STRUGGLING */}
            {step === "struggle" && (
              <motion.div key="struggle" {...fade} data-testid="bc-struggle">
                <h1 className="font-serif text-3xl text-olive mb-2 text-center">Still right here with you</h1>
                <p className="text-olive/70 mb-7 text-center">The urge will pass. Let's try one more thing.</p>
                <div className="space-y-3">
                  <button data-testid="bc-try-another" onClick={() => { setStep("interrupt"); fetchIntervention(trigger, shown); }}
                    className="w-full flex items-center gap-4 rounded-2xl bg-card border border-border p-4 text-left hover:bg-sage hover:border-brand-teal/40 transition-colors">
                    <div className="rounded-xl bg-sage p-2.5"><RotateCcw className="w-5 h-5 text-brand-teal-dark" /></div>
                    <span className="text-olive font-medium">Try another interruption</span>
                  </button>
                  <button data-testid="bc-toolkit" onClick={toToolkit}
                    className="w-full flex items-center gap-4 rounded-2xl bg-card border border-border p-4 text-left hover:bg-sage hover:border-brand-teal/40 transition-colors">
                    <div className="rounded-xl bg-sage p-2.5"><Wrench className="w-5 h-5 text-brand-teal-dark" /></div>
                    <span className="text-olive font-medium">Open your coping tools</span>
                  </button>
                  <button data-testid="bc-companion" onClick={toCompanion}
                    className="w-full flex items-center gap-4 rounded-2xl bg-card border border-border p-4 text-left hover:bg-sage hover:border-brand-teal/40 transition-colors">
                    <div className="rounded-xl bg-sage p-2.5"><MessageCircleHeart className="w-5 h-5 text-brand-teal-dark" /></div>
                    <span className="text-olive font-medium">Talk it through with Companion</span>
                  </button>
                  <button data-testid="bc-ask-pray" onClick={() => setStep("askpray")}
                    className="w-full flex items-center gap-4 rounded-2xl bg-card border border-border p-4 text-left hover:bg-sage hover:border-brand-teal/40 transition-colors">
                    <div className="rounded-xl bg-sage p-2.5"><HandHeart className="w-5 h-5 text-brand-teal-dark" /></div>
                    <span className="text-olive font-medium">Ask someone to pray with me</span>
                  </button>
                </div>
                <QuietLog id="bc-log-struggle" />
              </motion.div>
            )}

            {step === "askpray" && (
              <motion.div key="askpray" {...fade} data-testid="bc-askpray">
                {!prayerSent ? (
                  <>
                    <div className="rounded-2xl bg-brand-teal/15 w-14 h-14 flex items-center justify-center mx-auto mb-5"><HandHeart className="w-7 h-7 text-brand-teal-dark" /></div>
                    <h1 className="font-serif text-3xl text-olive mb-2 text-center">Ask someone to pray with you</h1>
                    <p className="text-olive/70 mb-6 text-center">We'll place a gentle request on the Prayer Wall — no details, just that you're facing a craving right now.</p>
                    <button data-testid="bc-share-name-toggle" onClick={() => setShareName((s) => !s)}
                      className="w-full flex items-center justify-between rounded-2xl bg-card border border-border p-4 mb-3">
                      <span className="text-olive text-sm">{shareName ? "Share my first name" : "Post anonymously"}</span>
                      <span className={`relative w-11 h-6 rounded-full transition-colors ${shareName ? "bg-brand-teal" : "bg-stone/40"}`}>
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${shareName ? "translate-x-5" : ""}`} />
                      </span>
                    </button>
                    <button data-testid="bc-send-prayer" onClick={sendPrayer} disabled={sendingPrayer}
                      className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-white font-medium py-4 flex items-center justify-center gap-2 disabled:opacity-60">
                      {sendingPrayer ? "Sending…" : "Send prayer request"}
                    </button>
                    <button data-testid="bc-askpray-back" onClick={() => setStep("struggle")} className="w-full text-sm text-stone mt-3 hover:underline">Back</button>
                  </>
                ) : (
                  <div className="text-center">
                    <div className="rounded-2xl bg-brand-teal/15 w-16 h-16 flex items-center justify-center mx-auto mb-5"><Check className="w-8 h-8 text-brand-teal-dark" /></div>
                    <h1 className="font-serif text-3xl text-olive mb-3">You're not alone.</h1>
                    <p className="text-olive/70 mb-7">Your request is on the wall. Others in the Not By Will community can pray with you now — come back any time to see them standing with you.</p>
                    <button data-testid="bc-view-wall" onClick={() => { navigate("/app/together"); onClose(); }}
                      className="w-full rounded-full bg-brand-navy text-brand-ivory font-medium py-4 flex items-center justify-center gap-2 mb-3">
                      <HandHeart className="w-4 h-4 text-brand-teal" /> See the Prayer Wall
                    </button>
                    <button data-testid="bc-askpray-done" onClick={onClose} className="w-full text-sm text-stone hover:underline">Close for now</button>
                  </div>
                )}
                <QuietLog id="bc-log-askpray" />
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
